/** Retry / backoff constants for sites the per-domain retry SSOTs (`r2-retry.ts`, `observability-retry.ts`) do not
 * cover: host-filesystem lock recovery, the FlowRunner module-load guard, and the shared jitter ceiling. Each symbol
 * names its own INTENT rather than a generic tier, which avoids the silent off-by-one between `maxAttempts` and
 * `maxRetries`. Paired forbid-rules: `no-retry-backoff-literals` and its compliance twin
 * `QUAL-NO-RETRY-BACKOFF-LITERALS-001`, both of which skip THIS leaf by path -- a rename here must update both. */

/** Base delay for retrying filesystem operations that fail with Windows-lock errors (EBUSY / EPERM / EACCES / ENOENT
 * during temp-file-then-rename). Calibrated for the AV / Defender / wrangler-SQLite lock window; NOT a generic HTTP knob. */
export const FS_RETRY_BASE_DELAY_MS = 100;

/** Delay before retrying a flows module load that failed with a transient peer-edit signature (untracked siblings,
 * in-flight imports, rename windows, skeleton-pending exports). One symbol so every consumer of the recovery path
 * absorbs the same parallel-agent edit window. */
export const FLOWRUNNER_GUARD_TRANSIENT_RETRY_DELAY_MS = 2500;

/** Jitter ceiling added on top of the exponential backoff base delay to prevent thundering-herd reconnection storms (`Math.random() * N`). */
export const DEFAULT_BACKOFF_JITTER_MAX_MS = 1000;

/**
 * Declaration-NAME shapes identifying a FlowRunner retry-delay constant -- arm (b) of the two forbid-rules named in the
 * module docstring, shared so the scan lane and the editor lane cannot recognize different names under one rule name.
 * Homed HERE rather than in a new leaf because both consumers already import this module for the guard value above,
 * and because both rules skip this leaf by path, so the SSOT cannot self-fire on the very alternation it publishes.
 * Case-INSENSITIVE, covering the camelCase and SCREAMING_SNAKE spellings from one source; no `/g` -- both consumers
 * call `.test` once per candidate name, and a `g` flag would leak `lastIndex` between them.
 */
export const FLOWRUNNER_RETRY_DELAY_NAME_RE = /(?:RETRY_DELAY_MS|FLOWS_RETRY_DELAY|FLOWRUNNER.*RETRY)/i;
